import { provideIcons } from '@ng-icons/core';
import {
  lucideCheck,
  lucideChevronDown,
  lucideChevronLeft,
  lucideChevronRight,
  lucideChevronUp,
  lucideCircle,
  lucideEllipsisVertical,
  lucideEye,
  lucideEyeOff,
  lucideFileText,
  lucideFilter,
  lucideInfo,
  lucideMic,
  lucideMinus,
  lucidePencil,
  lucidePlus,
  lucidePrinter,
  lucideSearch,
  lucideSquare,
  lucideStar,
  lucideTrash2,
  lucideTriangleAlert,
  lucideX,
} from '@ng-icons/lucide';

const iconMatchers: Array<{ matcher: RegExp; name: string }> = [
  { matcher: /(chevron-left|keyboard_arrow_left)/i, name: 'lucideChevronLeft' },
  { matcher: /(chevron-right|keyboard_arrow_right)/i, name: 'lucideChevronRight' },
  { matcher: /(chevron-down|keyboard_arrow_down)/i, name: 'lucideChevronDown' },
  { matcher: /(chevron-up|keyboard_arrow_up)/i, name: 'lucideChevronUp' },
  { matcher: /(x-mark|close|cancel)/i, name: 'lucideX' },
  { matcher: /(magnifying|search)/i, name: 'lucideSearch' },
  { matcher: /(microphone|mic)/i, name: 'lucideMic' },
  { matcher: /(stop)/i, name: 'lucideSquare' },
  { matcher: /(ellipsis-vertical|more-vertical)/i, name: 'lucideEllipsisVertical' },
  { matcher: /(eye-slash|eye-off|visibility_off)/i, name: 'lucideEyeOff' },
  { matcher: /(eye|visibility)/i, name: 'lucideEye' },
  { matcher: /(funnel|filter)/i, name: 'lucideFilter' },
  { matcher: /(star|favorite)/i, name: 'lucideStar' },
  { matcher: /(plus|add)/i, name: 'lucidePlus' },
  { matcher: /(minus|remove)/i, name: 'lucideMinus' },
  { matcher: /(check|done)/i, name: 'lucideCheck' },
  { matcher: /(edit|pencil)/i, name: 'lucidePencil' },
  { matcher: /(trash|delete)/i, name: 'lucideTrash2' },
  { matcher: /(document|file|xls|pdf)/i, name: 'lucideFileText' },
  { matcher: /(print|printer)/i, name: 'lucidePrinter' },
  { matcher: /(warning|alert)/i, name: 'lucideTriangleAlert' },
  { matcher: /(info)/i, name: 'lucideInfo' },
];

export const SVZ_LEGACY_ICON_PROVIDERS = [
  provideIcons({
    lucideCheck,
    lucideChevronDown,
    lucideChevronLeft,
    lucideChevronRight,
    lucideChevronUp,
    lucideCircle,
    lucideEllipsisVertical,
    lucideEye,
    lucideEyeOff,
    lucideFilter,
    lucideFileText,
    lucideInfo,
    lucideMinus,
    lucideMic,
    lucidePencil,
    lucidePlus,
    lucidePrinter,
    lucideSearch,
    lucideSquare,
    lucideStar,
    lucideTrash2,
    lucideTriangleAlert,
    lucideX,
  }),
];

/** Already-valid ng-icon keys (our `svcXxx` design-system set, or a correctly-cased `lucideXxx` name) —
 *  pass through unresolved rather than forcing them through the legacy-name matcher below, which has no
 *  coverage for most DS icon concepts (list/grid/kanban/calendar/...) and would silently fall back to
 *  `lucideCircle`. Found via the Action Plan search redesign, `svz-view-toggle`'s view-switch icons
 *  (`svcViewList`/`svcViewKanban`/`svcViewCalendar`) rendered as blank circles until this bypass existed.
 *  AB#73413 hit the same fall-through from the Alerts side and fixed it with an `icon in SOLVACE_ICONS`
 *  test; this pattern subsumes it, since every design-system key is an `svcXxx` name. */
const VALID_NG_ICON_KEY = /^(svc|lucide)[A-Z]/;

export function resolveLegacyIconName(icon?: string | null): string {
  if (!icon?.trim()) {
    return 'lucideX';
  }

  if (VALID_NG_ICON_KEY.test(icon)) {
    return icon;
  }

  const matchingIcon = iconMatchers.find(({ matcher }) => matcher.test(icon));
  return matchingIcon?.name ?? 'lucideCircle';
}
